import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clientActions } from 'core/client/clientReducers'
import useReactRouter from 'use-react-router'
import useListAction from 'core/hooks/useListAction'
import { listPodDisruptionBudgets, updatePodDisruptionBudget } from '../actions'
import { IPodDisruptionBudgetDetailsPageTabs, IPodDisruptionBudgetSelector } from '../model'
import { podDisruptionBudgetsSelector } from '../selectors'
import useUpdateAction from 'core/hooks/useUpdateAction'
import DocumentMeta from 'core/components/DocumentMeta'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import { routes } from 'core/utils/routes'
import PodDisruptionBudgetsOverview from './PodDisruptionBudgetsOverview'
import EntityYamlPage from 'k8s/components/common/entity/entity-yaml-page'
import ApiClient from 'api-client/ApiClient'
import { isEmpty } from 'ramda'
import jsYaml from 'js-yaml'
import { pathStrOr } from 'utils/fp'
import OverviewActions from 'core/elements/overview/OverviewActions'
import DeletePodDisruptionBudgetDialog from '../DeletePodDisruptionBudgetDialog'
import usePluginRouter from 'core/hooks/usePluginRouter'

const { qbert } = ApiClient.getInstance()

const actions = {
  rightActions: [
    {
      label: 'Delete',
      icon: 'trash-alt',
      DialogComponent: DeletePodDisruptionBudgetDialog,
    },
  ],
}

export default function PodDisruptionBudgetsDetailsPage() {
  const dispatch = useDispatch()
  const { currentPluginId } = usePluginRouter()
  const routePath = currentPluginId === 'kubevirt' ? 'kubevirtConfiguration' : 'configuration'
  const { match } = useReactRouter()
  const { id, clusterId } = match.params
  const { loading, reload } = useListAction(listPodDisruptionBudgets, { params: { clusterId } })
  const podDisruptionBudgets: IPodDisruptionBudgetSelector[] = useSelector(
    podDisruptionBudgetsSelector,
  )
  const podDisruptionBudget = useMemo(
    () => podDisruptionBudgets.find((podDisruptionBudget) => podDisruptionBudget.id === id),
    [id, podDisruptionBudgets],
  )

  const { update, updating } = useUpdateAction(updatePodDisruptionBudget)

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        clusterId: podDisruptionBudget?.clusterName || clusterId,
        id: podDisruptionBudget?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [podDisruptionBudget?.clusterName, podDisruptionBudget?.name, id, clusterId])

  const getPodDisruptionBudgetYaml = useCallback(async () => {
    if (isEmpty(podDisruptionBudget)) return undefined
    return qbert.getPodDisruptionBudget(
      podDisruptionBudget.clusterId,
      podDisruptionBudget.namespace,
      podDisruptionBudget.name,
    )
  }, [podDisruptionBudget])

  const handlePodDisruptionBudgetUpdate = async (yaml) => {
    const body = jsYaml.load(yaml)
    const namespace = pathStrOr('', 'metadata.namespace', body)
    const name = pathStrOr('', 'metadata.name', body)
    await update({
      id,
      clusterId,
      namespace,
      name,
      body,
    })
  }

  return (
    <>
      <DocumentMeta title="Pod Disruption Budgets Overview" breadcrumbs />
      <div>
        <OverviewActions actions={actions} entity={podDisruptionBudget} />
      </div>
      <Tabs route={routes[routePath].podDisruptionBudgets.details}>
        <Tab label="Overview" value={IPodDisruptionBudgetDetailsPageTabs.Overview}>
          <PodDisruptionBudgetsOverview
            podDisruptionBudget={podDisruptionBudget}
            loading={loading}
            reload={reload}
          />
        </Tab>
        <Tab label="YAML" value={IPodDisruptionBudgetDetailsPageTabs.Yaml}>
          <EntityYamlPage
            entityType="Pod Disruption Budgets"
            entityName={podDisruptionBudget?.name}
            getYamlFn={getPodDisruptionBudgetYaml}
            handleUpdate={handlePodDisruptionBudgetUpdate}
            loading={loading || updating}
          />
        </Tab>
      </Tabs>
    </>
  )
}
